import * as THREE from 'three';
import type { AnimatedModelData } from '@/utils/AnimatedModelLoader';
import { logger } from '@/utils/logger';

export type AnimationState = 'running' | 'jumping';

export class AnimationStateManager {
  private mixer: THREE.AnimationMixer;
  private actions: Map<AnimationState, THREE.AnimationAction>;
  private currentState: AnimationState;
  private readonly fadeTime = 0.2; // クロスフェード時間（秒）

  constructor(model: THREE.Object3D, animations: AnimatedModelData['animations']) {
    // AnimationMixerの作成
    this.mixer = new THREE.AnimationMixer(model);
    this.actions = new Map();
    this.currentState = 'running';

    // 各アニメーションのActionを作成
    this.setupAction('running', animations.running, THREE.LoopRepeat);
    this.setupAction('jumping', animations.jumping, THREE.LoopOnce);

    // ジャンプアニメーションは最後のフレームで停止
    const jumpAction = this.actions.get('jumping');
    if (jumpAction) {
      jumpAction.clampWhenFinished = true;
    }

    // 初期状態（running）を再生
    const runningAction = this.actions.get('running');
    if (runningAction) {
      runningAction.play();
    }
  }

  /**
   * AnimationActionをセットアップ
   */
  private setupAction(
    state: AnimationState,
    clip: THREE.AnimationClip,
    loopMode: THREE.AnimationActionLoopStyles
  ): void {
    // Y方向のルート移動を除去したクリップを作成
    const tracks = clip.tracks.filter(track => {
      const trackNameLower = track.name.toLowerCase();

      // 様々なパターンでY位置トラックを検出
      const isYPosition = (
        // パターン1: .position.y または .position[1]
        (trackNameLower.includes('position') && (trackNameLower.endsWith('.y') || trackNameLower.includes('[1]'))) ||
        // パターン2: hip系のbone + position
        (trackNameLower.includes('hip') && trackNameLower.includes('position')) ||
        // パターン3: root系のbone + y
        (trackNameLower.includes('root') && trackNameLower.includes('position') && trackNameLower.includes('y'))
      );

      return !isYPosition;
    });

    const modifiedClip = new THREE.AnimationClip(clip.name, clip.duration, tracks);

    const action = this.mixer.clipAction(modifiedClip);
    action.loop = loopMode;
    this.actions.set(state, action);
  }

  /**
   * アニメーション状態を変更
   */
  public setState(newState: AnimationState, immediate: boolean = false): void {
    if (newState === this.currentState) {
      return;
    }

    const previousAction = this.actions.get(this.currentState);
    const newAction = this.actions.get(newState);

    if (!previousAction || !newAction) {
      logger.error(`Animation action not found: ${this.currentState} or ${newState}`);
      return;
    }

    if (immediate) {
      // 即座に切り替え
      previousAction.stop();
      newAction.reset().play();
    } else {
      // スムーズにクロスフェード
      newAction.reset().fadeIn(this.fadeTime).play();
      previousAction.fadeOut(this.fadeTime);

      // ジャンプアニメーションの場合、速度を調整（オプション）
      if (newState === 'jumping') {
        // ジャンプアニメーションを少し早く再生（ゲーム物理に合わせる）
        newAction.timeScale = 1.2;
      } else {
        newAction.timeScale = 1.0;
      }
    }

    this.currentState = newState;
  }

  /**
   * 現在のアニメーション状態を取得
   */
  public getCurrentState(): AnimationState {
    return this.currentState;
  }

  /**
   * AnimationMixerを更新（毎フレーム呼び出す）
   */
  public update(deltaTime: number): void {
    this.mixer.update(deltaTime);
  }

  /**
   * クリーンアップ
   */
  public dispose(): void {
    this.mixer.stopAllAction();
    this.actions.clear();
  }
}
